import { Component } from '@theme/component';

/**
 * Ensures rich text links have an accessible name when merchant-authored
 * content leaves the anchor visually empty.
 * @param {ParentNode} root
 */
function formatLinks(root) {
  root.querySelectorAll('a[href]').forEach((link) => {
    const hasAccessibleName =
      link.textContent.trim() ||
      link.getAttribute('aria-label') ||
      link.getAttribute('title') ||
      link.querySelector('img[alt]:not([alt=""])');

    if (hasAccessibleName) return;

    const href = link.getAttribute('href') || '';
    let label = href;

    if (href.startsWith('mailto:')) {
      label = href.replace(/^mailto:/, '').split('?')[0];
    } else if (href.startsWith('tel:')) {
      label = href.replace(/^tel:/, '');
    }

    link.setAttribute('aria-label', label);
  });
}

/**
 * Adds header cells to merchant-authored tables that only use td cells.
 * @param {HTMLTableElement} table
 */
function repairTableHeaders(table) {
  const existingHeaders = table.querySelectorAll('th');

  if (existingHeaders.length) {
    existingHeaders.forEach((header) => {
      if (!header.hasAttribute('scope')) header.setAttribute('scope', 'col');
    });
    return;
  }

  const firstRow = table.querySelector('tr');
  if (!firstRow) return;

  Array.from(firstRow.children).forEach((cell) => {
    if (cell.tagName.toLowerCase() !== 'td') return;

    const header = document.createElement('th');
    Array.from(cell.attributes).forEach((attribute) => {
      header.setAttribute(attribute.name, attribute.value);
    });
    header.setAttribute('scope', 'col');
    header.innerHTML = cell.innerHTML;
    cell.replaceWith(header);
  });
}

/**
 * Formats a table for easier styling
 * @param {HTMLTableElement} table
 */
function formatTable(table) {
  repairTableHeaders(table);

  if (table.parentElement?.classList.contains('rte-table-wrapper')) return;

  const wrapper = document.createElement('div');
  wrapper.classList.add('rte-table-wrapper');
  const parent = table.parentNode;
  if (parent) {
    parent.insertBefore(wrapper, table);
    wrapper.appendChild(table);
  }
}

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    formatLinks(this);
    this.querySelectorAll('table').forEach(formatTable);
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.shopify-policy__body').forEach((policyBody) => {
    formatLinks(policyBody);
    policyBody.querySelectorAll('table').forEach(formatTable);
  });
});
